import { observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { allOptionsValue } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import CustomSelect from 'src/view/components/Controls/Pickers/CustomSelect/CustomSelect';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types/index';
import SpinLoader from 'src/view/components/Controls/SpinLoader/SpinLoader';
import { ReportsBranchPicker } from 'src/view/screens/ReportsScreen/components/ReportsBranchPicker/ReportsBranchPicker';
import ReportsCalendar from 'src/view/screens/ReportsScreen/components/ReportsCalendar/ReportsCalendar';
import ReportsFranchiseePicker from 'src/view/screens/ReportsScreen/components/ReportsFranchiseePicker/ReportsFranchiseePicker';
import { defaultPeriodReportFilters, salesPeriodReportColumnTooltips } from 'src/view/screens/ReportsScreen/constants/reprots';
import ReportContent from 'src/view/screens/ReportsScreen/containers/ReportContent/ReportContent';
import ReportHeader from 'src/view/screens/ReportsScreen/containers/ReportHeader/ReportHeader';
import { salesPeriodStore } from 'src/view/screens/ReportsScreen/screens/SalesPeriod/store/salesPeriodStore';
import {
  COMPARED_PERIOD,
  IDailyReportParams,
  TIME_REPORTS_COMPARED_COLUMN,
} from 'src/view/screens/ReportsScreen/screens/SalesPeriod/types/salesPeriod';
import { reportsStore } from 'src/view/screens/ReportsScreen/store/reportsStore';
import { appStore } from '~/mobx/appStore';

@observer
export default class DayWeekSales extends React.Component<{}, {}> {
  get weekDaysOptions(): IPickerOption[] {
    const weekDays = moment.weekdays();
    // push Sunday to the end if needed
    // weekDays.push(weekDays.shift() as string);
    return [
      { label: localeStore.t('every_day_of_week'), value: allOptionsValue },
      ...weekDays.map((name, index) => ({ label: localeStore.t(`txt_${name.toLowerCase()}`), value: index })),
    ];
  }

  async componentDidMount() {
    if (!salesPeriodStore.reports.daily.current_period) {
      await this.buildReports();
    }
  }

  onSelectDays = (options: IPickerOption[]) => {
    salesPeriodStore.setFilters({ days: options });
  };

  onGenerateReport = async () => {
    await this.buildReports();
  };

  onExportReport = async () => {
    const params: IDailyReportParams = {
      ...reportsStore.getReportBuildParams(),
      days: salesPeriodStore.filters.days.some((option) => option.value === allOptionsValue)
        ? []
        : salesPeriodStore.filters.days.map((option) => option.value as number),
    };
    await salesPeriodStore.exportPeriodReportToXls(params, 'daily', 'Daily sales');
  };

  buildReports = async () => {
    await salesPeriodStore.fetchDailyReport(true);
    // build comparative report
    if (salesPeriodStore.reports.daily.current_period && !salesPeriodStore.reports.daily.previous_period) {
      await salesPeriodStore.fetchDailyReport(false);
    }
  };

  onCancel = () => {
    if (salesPeriodStore.cancelReportBuild.daily) salesPeriodStore.cancelReportBuild.daily('Cancelled');
  };

  render() {
    const { startDate, endDate } = reportsStore.filters;
    return (
      <div className="d-flex flex-column full-width flex-grow-1 mt-4">
        <ReportHeader
          onExportReport={this.onExportReport}
          comparedPeriod={{ startDate, endDate }}
          onGenerateReport={this.onGenerateReport}
          exportLoader={!!salesPeriodStore.reports.daily.loading}
        >
          <ReportsCalendar
            startDate={startDate}
            endDate={endDate}
            periodComparingOptions={[
              { period: COMPARED_PERIOD.YEAR, label: 'compare_with_the_previous_year' },
              { period: COMPARED_PERIOD.MONTH, label: 'compare_with_the_previous_month' },
            ]}
          />
          <CustomSelect
            className="mr-2"
            inputClassName="report-header__picker"
            onSelect={this.onSelectDays}
            options={this.weekDaysOptions}
            selectedOptions={salesPeriodStore.filters.days}
            defaultOptions={defaultPeriodReportFilters.days}
            optionsDesc="days"
            isMulti
          />
          {appStore.isAddonsAvailable.franchise ? <ReportsFranchiseePicker /> : <ReportsBranchPicker />}
        </ReportHeader>
        <ReportContent
          noSorting
          actualReport={salesPeriodStore.reports.daily.current_period}
          comparativeReport={salesPeriodStore.reports.daily.previous_period}
          reportColumnName={TIME_REPORTS_COMPARED_COLUMN.DAY_WEEK}
          columnTooltips={salesPeriodReportColumnTooltips}
        />
        {salesPeriodStore.reports.daily.loading ? <SpinLoader onCancel={this.onCancel} /> : null}
      </div>
    );
  }
}
